import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import FeedApprovals, { ApKind, Approval, ApState, TsEntry, EntryState, Person } from './FeedApprovals';
import FeedRow, { Tone } from './FeedRow';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface Props { auth: boolean; access: any; currency?: string | null; baseUrl?: string; }

type TabId = 'all' | 'training' | 'approvals' | 'notifs';

interface Course {
  id: number; title: string; passThreshold: number; isMandatory?: boolean; pageCount: number;
  lastResult: { score: number; completedAt: string } | null; dueAt?: string | null;
}
interface Notice {
  id: number; title?: string; message: string; source?: string; severity?: string;
  link?: string; status: string; createdAt: string;
}

type Item =
  | { kind: 'tr'; key: string; ts: number | null; tr: Course }
  | { kind: 'no'; key: string; ts: number | null; no: Notice };

const on = (v: any) => v === true || v === 1;

const SEV_TONE: Record<string, Tone> = { warning: 'amber', success: 'green', info: 'cyan', error: 'red' };

function noticeIcon(n: Notice) {
  const s = (n.source || '').toLowerCase();
  if (s.includes('timesheet') || s.includes('report') || s.includes('reject')) return ScheduleOutlinedIcon;
  if (s.includes('expense') || s.includes('finance')) return ReceiptLongOutlinedIcon;
  if (s.includes('leave') || s.includes('absence')) return EventOutlinedIcon;
  if (s.includes('training') || s.includes('course')) return SchoolOutlinedIcon;
  if (s.includes('booking') || s.includes('room')) return MeetingRoomOutlinedIcon;
  if (s.includes('document')) return DescriptionOutlinedIcon;
  return NotificationsOutlinedIcon;
}

const coursePct = (c: Course) => {
  if (!c.lastResult) return 0;
  const score = c.lastResult.score ?? 0;
  return score >= (c.passThreshold || 70) ? 100 : Math.round(score);
};
const isOverdue = (c: Course) => !!c.dueAt && new Date(c.dueAt).getTime() < Date.now();

const fmtDay = (d: string | Date) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
function timeAgo(ts: number | null): string {
  if (ts == null) return '';
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (moment(ts).isSame(moment(), 'day')) return `${h}h ago`;
  if (moment(ts).isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
  const d = Math.floor(h / 24);
  return d < 7 ? `${Math.max(d, 1)}d ago` : fmtDay(new Date(ts));
}
const tsOf = (d?: string | null) => (d ? new Date(d).getTime() || null : null);

const FeedCard: React.FC<Props> = ({ auth, access, currency, baseUrl = '' }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // ── Access (same rules as the source screens) ─────────────────────────────
  const canTraining = on(access?.training_view) || on(access?.training_admin);

  const [tab, setTab] = useState<TabId>('all');
  const [courses, setCourses] = useState<Course[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [apState, setApState] = useState<Record<string, ApState>>({});
  const [tsEntries, setTsEntries] = useState<TsEntry[]>([]);
  const [entryState, setEntryState] = useState<Record<number, EntryState>>({});
  const [people, setPeople] = useState<Record<number, Person>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [meId, setMeId] = useState<number | null>(null);
  // Approvals: only items addressed to me that my role may approve (the backend applies the same rights).
  // A section shows when it has items, or when I supervise someone AND have the right for that type.
  const [isSupervisor, setIsSupervisor] = useState(false);
  const isLeaveKind = (k: ApKind) => k === 'leave' || k === 'sickness' || k === 'lateness';
  const rightTs = on(access?.timesheet_approve_enabled) &&
    (on(access?.timesheet_approve_sv) || on(access?.timesheet_approve_dep) || on(access?.timesheet_approve_all));
  const rightLv = on(access?.hr_absence_approve) || on(access?.hr_absence_approve_team) || on(access?.hr_absence_approve_all);
  const rightEx = on(access?.expenses_manager) || on(access?.expenses_admin) || on(access?.expenses_approve) || on(access?.finance_admin);
  const showTs = tsEntries.length > 0 || (isSupervisor && rightTs);
  const showLv = approvals.some(a => isLeaveKind(a.kind)) || (isSupervisor && rightLv);
  const showEx = approvals.some(a => !isLeaveKind(a.kind)) || (isSupervisor && rightEx);
  const canApprove = showTs || showLv || showEx;
  // Notices unread at load time: they stay in "Unread" until the next load (no jumping)
  const [unreadAtLoad, setUnreadAtLoad] = useState<Set<number>>(new Set());

  const money = useCallback((n: number) => {
    try { return currency ? new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(n) : n.toFixed(2); }
    catch { return n.toFixed(2); }
  }, [currency]);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadTraining = useCallback(async () => {
    if (!canTraining) return;
    try {
      const { data } = await axios.get('training/assigned');
      setCourses((Array.isArray(data) ? data : []).filter((c: Course) => coursePct(c) < 100));
    } catch { /* handled by interceptor */ }
  }, [canTraining]);

  // Current user (id) + own notices come from GET user, as in App.tsx
  const loadMe = useCallback(async () => {
    try {
      const { data } = await axios.get('user');
      setMeId(data?.id ?? null);
      const list: Notice[] = Array.isArray(data?.notice) ? data.notice : [];
      setNotices([...list].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))));
      setUnreadAtLoad(new Set(list.filter(x => x.status === 'new').map(x => x.id)));
      return data?.id ?? null;
    } catch { return null; }
  }, []);

  // Everything here is filtered on the server: supervisorId = me, my company, status pending
  const loadApprovals = useCallback(async (me: number | null) => {
    if (me == null) return;
    const [absR, typesR, usersR, tsR, expR, carR] = await Promise.allSettled([
      axios.get('hr-absence/to-approve'),
      axios.get('hr-absence-type'),
      axios.get('users/supervisor'),
      axios.get('time-reports/to-approve/mine'),
      axios.get('expenses/to-approve/mine'),
      axios.get('cars-expenses/to-approve/mine'),
    ]);
    const val = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? r.value.data : null);
    const list: Approval[] = [];
    const fullName = (u: any) => (u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : '');

    // Direct reports (or all users with users_view_all): avatars, contract hours, "am I a supervisor"
    const users: any[] = Array.isArray(val(usersR)) ? val(usersR) : [];
    const types: any[] = Array.isArray(val(typesR)) ? val(typesR) : [];

    // Absences assigned to me
    (Array.isArray(val(absR)) ? val(absR) : []).forEach((a: any) => {
      const kind: ApKind = a.category === 'sickness' ? 'sickness' : a.category === 'lateness' ? 'lateness' : 'leave';
      const tp = types.find(x => x.id === a.typeId);
      const range = a.startDate === a.endDate ? fmtDay(a.startDate) : `${fmtDay(a.startDate)} – ${fmtDay(a.endDate)}`;
      const size = a.days ? `${+Number(a.days).toFixed(2)} day${Number(a.days) === 1 ? '' : 's'}` : a.hours ? `${+Number(a.hours).toFixed(2)}h` : '';
      list.push({
        key: `abs${a.id}`, kind, absenceId: a.id, userId: a.userId,
        name: fullName(users.find(u => u.id === a.userId)) || `Employee #${a.userId}`,
        what: size ? `${range} (${size})` : range,
        sub: [tp?.name, a.reason].filter(Boolean).join(' · '),
        ts: tsOf(a.createdAt),
      });
    });

    // Timesheet entries waiting for me
    const entryMinutes = (r: any) => {
      let d = moment(r.endTime).diff(moment(r.startTime), 'minutes');
      if (d < 0) d = moment(r.endTime).add(1, 'day').diff(moment(r.startTime), 'minutes');
      return Math.ceil(d / 5) * 5;
    };
    const entries: TsEntry[] = (Array.isArray(val(tsR)) ? val(tsR) : []).map((r: any) => {
      const d = moment(r.date);
      return {
        id: r.id, userId: r.userId, name: fullName(r.user) || `Employee #${r.userId}`,
        date: d.format('YYYY-MM-DD'), week: d.clone().startOf('isoWeek').format('YYYY-MM-DD'), di: d.isoWeekday() - 1,
        start: moment(r.startTime).format('HH:mm'), end: moment(r.endTime).format('HH:mm'), minutes: entryMinutes(r),
        category: String(r.categoryTree || r.category?.name || ''),
        task: Array.isArray(r.tasks) ? r.tasks.map((t: any) => t?.name).filter(Boolean).join(', ') : '',
        description: r.description || '',
      };
    });

    // Expense and mileage claims waiting for me
    (Array.isArray(val(expR)) ? val(expR) : []).forEach((e: any) => {
      list.push({
        key: `exp${e.id}`, kind: 'expense', expenseId: e.id, userId: e.userId ?? e.createdByUserId, receiptUrl: e.payment_document_url || null,
        name: fullName(e.user) || fullName(e.create_user) || 'Employee',
        what: money(Number(e.amount) || 0),
        sub: [e.description, e.date ? fmtDay(e.date) : ''].filter(Boolean).join(' · '),
        ts: tsOf(e.createdAt) ?? tsOf(e.date),
      });
    });
    const car = val(carR);
    (Array.isArray(car) ? car : []).forEach((c: any) => {
      const dist = c.distance != null ? `${+Number(c.distance).toFixed(1)} ${c.unit || ''}`.trim() : '';
      list.push({
        key: `car${c.id}`, kind: 'mileage', carId: c.id, userId: c.userId,
        name: fullName(c.user) || 'Employee',
        what: money(Number(c.cost) || 0),
        sub: [c.destination || c.purpose, dist, c.date ? fmtDay(c.date) : ''].filter(Boolean).join(' · '),
        ts: tsOf(c.createdAt) ?? tsOf(c.date),
      });
    });

    // People: avatars + weekly contract hours (pattern) for the TOIL chip
    const ppl: Record<number, Person> = {};
    users.forEach((u: any) => {
      ppl[u.id] = { id: u.id, first_name: u.first_name, last_name: u.last_name, avatarUrl: u.avatarUrl,
        contract: u.pattern?.hoursPerWeek != null ? Number(u.pattern.hoursPerWeek) || null : null };
    });

    setApprovals(list);
    setApState({});
    setTsEntries(entries);
    setEntryState({});
    setPeople(ppl);
    setIsSupervisor(users.some((u: any) => u.supervisorId === me));
  }, [money]);

  const loadAll = useCallback(async () => {
    loadTraining();
    const me = await loadMe();
    loadApprovals(me);
  }, [loadTraining, loadMe, loadApprovals]);

  useEffect(() => { if (auth) loadAll(); }, [auth, loadAll]);

  // Hidden tabs fall back to All
  useEffect(() => {
    if ((tab === 'training' && !canTraining) || (tab === 'approvals' && !canApprove)) setTab('all');
  }, [tab, canTraining, canApprove]);

  // ── Actions ───────────────────────────────────────────────────────────────
  // APIs have no revert → show the result briefly, then reload (one reload for a burst of actions)
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => { if (meId != null) loadApprovals(meId); }, 2500);
  }, [meId, loadApprovals]);
  useEffect(() => () => { if (reloadTimer.current) clearTimeout(reloadTimer.current); }, []);

  const finish = (key: string, state: ApState) => {
    setApState(s => ({ ...s, [key]: state }));
    scheduleReload();
  };
  const markEntries = (ids: number[], st: EntryState | null) =>
    setEntryState(s => {
      const n = { ...s };
      ids.forEach(id => { if (st) n[id] = st; else delete n[id]; });
      return n;
    });

  // Week table actions — same calls as PendingApprovalsPanel
  const approveEntries = async (ids: number[]) => {
    markEntries(ids, 'busy');
    try {
      await axios.put('/time-reports/update-status', { reportsId: ids, status: 'fulfilled' });
      markEntries(ids, 'approved');
      scheduleReload();
    } catch {
      markEntries(ids, null);
      enqueueSnackbar('Could not approve', { variant: 'error' });
    }
  };
  const returnEntries = async (ids: number[], comment: string, source: string, ownerId: number) => {
    markEntries(ids, 'busy');
    try {
      await axios.post('notice/add', { message: comment, source, sourceId: ids[0], severity: 'warning', userId: ownerId });
      await axios.put('/time-reports/update-status', { reportsId: ids, status: 'rejected', comments: comment });
      markEntries(ids, 'returned');
      enqueueSnackbar('Returned · the comment was sent as a notification', { variant: 'success' });
      scheduleReload();
    } catch {
      markEntries(ids, null);
      enqueueSnackbar('Could not return', { variant: 'error' });
    }
  };

  const approve = async (ap: Approval) => {
    setApState(s => ({ ...s, [ap.key]: 'busy' }));
    try {
      if (ap.absenceId) await axios.put(`hr-absence/${ap.absenceId}/approve`);
      else if (ap.expenseId) await axios.put('expenses/update/status', { status: 'fulfilled', id: [ap.expenseId] });
      else if (ap.carId) await axios.put('cars-expenses/update/status', { status: 'fulfilled', id: [ap.carId] });
      finish(ap.key, 'approved');
    } catch {
      setApState(s => { const n = { ...s }; delete n[ap.key]; return n; });
      enqueueSnackbar('Could not approve', { variant: 'error' });
    }
  };

  const reject = async (ap: Approval, reason: string) => {
    setApState(s => ({ ...s, [ap.key]: 'busy' }));
    try {
      if (ap.absenceId) await axios.put(`hr-absence/${ap.absenceId}/reject`, { reason });
      else if (ap.expenseId) await axios.put('expenses/update/status', { status: 'rejected', id: [ap.expenseId], status_comment: reason });
      else if (ap.carId) await axios.put('cars-expenses/update/status', { status: 'rejected', id: [ap.carId], status_comment: reason });
      finish(ap.key, 'rejected');
    } catch {
      setApState(s => { const n = { ...s }; delete n[ap.key]; return n; });
      enqueueSnackbar('Could not reject', { variant: 'error' });
    }
  };

  const markSeen = useCallback(async (n: Notice) => {
    if (n.status !== 'new') return;
    setNotices(list => list.map(x => (x.id === n.id ? { ...x, status: 'seen' } : x)));
    try { await axios.put(`notice/${n.id}/seen`); }
    catch { setNotices(list => list.map(x => (x.id === n.id ? { ...x, status: 'new' } : x))); }
  }, []);

  const markAllSeen = () => notices.filter(n => n.status === 'new').forEach(markSeen);

  const dismiss = async (n: Notice) => {
    try {
      await axios.delete(`notice/${n.id}`);
      setNotices(list => list.filter(x => x.id !== n.id));
    } catch { enqueueSnackbar('Could not dismiss', { variant: 'error' }); }
  };

  const toggle = (it: Item) => {
    setOpen(o => ({ ...o, [it.key]: !o[it.key] }));
    if (it.kind === 'no') markSeen(it.no);
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const k = {
      training: canTraining ? courses.length : 0,
      // time entries + other requests still waiting (same unit as the Approvals tab)
      approvals: canApprove
        ? tsEntries.filter(e => !entryState[e.id] || entryState[e.id] === 'busy').length
          + approvals.filter(a => !apState[a.key] || apState[a.key] === 'busy').length
        : 0,
      notifs: notices.filter(n => n.status === 'new').length,
    };
    return { ...k, all: k.training + k.approvals + k.notifs };
  }, [courses, approvals, apState, tsEntries, entryState, notices, canTraining, canApprove]);

  const items = useMemo(() => {
    const byTime = (a: Item, b: Item) => (b.ts ?? 0) - (a.ts ?? 0);
    const tr: Item[] = canTraining ? courses.map(c => ({ kind: 'tr' as const, key: `tr${c.id}`, ts: tsOf(c.lastResult?.completedAt), tr: c })) : [];
    const no: Item[] = notices.map(n => ({ kind: 'no' as const, key: `no${n.id}`, ts: tsOf(n.createdAt), no: n }));
    if (tab === 'training') return tr.sort((a, b) => Number(isOverdue((b as any).tr)) - Number(isOverdue((a as any).tr)));
    if (tab === 'notifs') return no.sort(byTime);
    return [...no, ...tr].sort(byTime);
  }, [courses, notices, tab, canTraining]);

  // Unread (at load) first, then everything else; Training has one plain list
  const groups = useMemo(() => {
    if (tab === 'training') return items.length ? [{ name: '', list: items }] : [];
    const isUnread = (i: Item) => i.kind === 'no' && unreadAtLoad.has(i.no.id);
    return [
      { name: 'Unread', list: items.filter(isUnread) },
      { name: 'Earlier', list: items.filter(i => !isUnread(i)) },
    ].filter(g => g.list.length);
  }, [items, tab, unreadAtLoad]);

  const tabs: { id: TabId; label: string; n: number }[] = [
    { id: 'all', label: 'All', n: counts.all },
    ...(canTraining ? [{ id: 'training' as const, label: 'Training', n: counts.training }] : []),
    ...(canApprove ? [{ id: 'approvals' as const, label: 'Approvals', n: counts.approvals }] : []),
    { id: 'notifs', label: 'Notifications', n: counts.notifs },
  ];

  // ── Rows (one anatomy, see FeedRow) ───────────────────────────────────────
  const renderItem = (it: Item) => {
    if (it.kind === 'tr') {
      const c = it.tr;
      const pct = coursePct(c);
      const od = isOverdue(c);
      const due = c.dueAt ? (od ? `Overdue since ${fmtDay(c.dueAt)}` : `Due ${fmtDay(c.dueAt)}`) : (pct > 0 ? 'In progress' : 'Not started');
      return (
        <FeedRow key={it.key} tone={od ? 'red' : 'purple'} icon={<SchoolOutlinedIcon />} title={c.title}
          chips={<>
            <span className="fd-chip fd-t-purple">Training</span>
            {c.isMandatory && <span className="fd-chip fd-t-mu">Mandatory</span>}
          </>}
          sub={<span className="fd-row"><span className={`fd-bar${od ? ' red' : ''}`}><i style={{ width: `${Math.max(pct, 2)}%` }} /></span>
            <span className={od ? 'red' : ''}>{pct}% · {due}</span></span>}
          meta={<button type="button" className={`fd-btn ${od ? 'pri' : 'soft'}`} onClick={() => navigate('/training')}>
            <PlayArrowIcon />{pct > 0 ? 'Continue' : 'Start'}</button>} />
      );
    }
    const n = it.no;
    const unread = n.status === 'new';
    const Icon = noticeIcon(n);
    const tone: Tone = SEV_TONE[n.severity || ''] || 'cyan';
    return (
      <FeedRow key={it.key} tone={tone} icon={<Icon />} title={n.title || n.source || 'Notification'}
        chips={n.title && n.source ? <span className={`fd-chip fd-t-${tone}`}>{n.source}</span> : null}
        sub={n.message} unread={unread} muted={!unread} expandable open={!!open[it.key]} onToggle={() => toggle(it)}
        meta={<>{unread && <span className="fd-dot" />}<span className="fd-time">{timeAgo(it.ts)}</span></>}>
        <div className="fd-prose">{n.message}</div>
        <div className="fd-row" style={{ marginTop: 8 }}>
          {n.link && <button type="button" className="fd-btn pri" onClick={() => navigate(n.link!)}>Open<ArrowForwardIcon /></button>}
          <button type="button" className="fd-link" onClick={() => dismiss(n)}>Dismiss</button>
        </div>
      </FeedRow>
    );
  };

  const unreadNow = counts.notifs;
  const timeline = groups.map(g => (
    <React.Fragment key={g.name || 'list'}>
      {g.name && (
        <div className="fd-grp">{g.name}{g.name === 'Unread' && unreadNow > 0 && <span className="fd-ucnt">{unreadNow}</span>}</div>
      )}
      {g.list.map(renderItem)}
    </React.Fragment>
  ));

  const empty: Record<TabId, string> = {
    all: 'You are all caught up', training: 'No unfinished training',
    approvals: 'Nothing waiting for your decision', notifs: 'No notifications',
  };

  return (
    <>
      <div className="dashboard-card fd-card">
        <div className="fd-head">
          <div className="fd-ic"><AppsOutlinedIcon /></div>
          <span className="fd-title">My feed</span>
          <div className="fd-sp" />
          <div className="fd-seg" role="tablist">
            {tabs.map(tb => (
              <button key={tb.id} type="button" role="tab" aria-selected={tab === tb.id}
                className={tab === tb.id ? 'on' : ''} onClick={() => setTab(tb.id)}>
                {tb.label}{tb.n > 0 && <span className="n">{tb.n}</span>}
              </button>
            ))}
          </div>
        </div>

        {tab === 'notifs' && (
          <div className="fd-tool">
            <span className="fd-t2">{counts.notifs} unread</span>
            <div className="fd-sp" />
            {counts.notifs > 0 && (
              <button type="button" className="fd-btn gh" onClick={markAllSeen}><DoneAllIcon />Mark all read</button>
            )}
          </div>
        )}

        {tab === 'approvals' || (tab === 'all' && canApprove) ? (
          <FeedApprovals
            entries={tsEntries} entryState={entryState}
            others={approvals} apState={apState}
            people={people} baseUrl={baseUrl}
            showTs={showTs} showLv={showLv} showEx={showEx}
            onApproveEntries={approveEntries} onReturnEntries={returnEntries}
            onApprove={approve} onReject={reject}
            title={tab === 'all' ? 'Needs your action' : undefined}
            after={tab === 'all' ? timeline : undefined}
          />
        ) : (
          <div className="fd-scroll">
            {groups.length === 0 && <div className="fd-empty">{empty[tab]}</div>}
            {timeline}
            <div style={{ height: 8 }} />
          </div>
        )}
      </div>
    </>
  );
};

export default FeedCard;
